import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { PaperPlaneRight, Sparkle, Trash, Plus, Robot, Lightning, TrendUp, ChartLineUp } from '@phosphor-icons/react';

const SUGGESTIONS = [
  { icon: TrendUp, label: 'BTCUSD idea', q: 'Give me a BTCUSD trade idea' },
  { icon: ChartLineUp, label: 'Gold analysis', q: 'XAUUSD analysis please' },
  { icon: Lightning, label: 'BTC buy setup', q: 'Any BTC buy setup right now?' },
  { icon: Sparkle, label: 'Gold sell signal', q: 'Is there a gold sell signal?' },
];

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  const badge = msg.symbol && msg.used_live_data;
  // Simple formatter: convert **bold** and preserve line breaks
  const html = (msg.content || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/`([^`]+?)`/g, '<code class="text-saffron font-mono text-xs">$1</code>')
    .replace(/-{20,}/g, '<hr class="border-white/10 my-2" />');
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-lg p-4 ${isUser ? 'bg-saffron text-white' : 'glass'}`}>
        {!isUser && (
          <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/5">
            <div className="w-6 h-6 rounded gradient-orange flex items-center justify-center">
              <Robot size={14} weight="fill" className="text-white" />
            </div>
            <span className="text-xs font-heading font-semibold">Smart Trade AI</span>
            {badge && <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 uppercase tracking-widest">Live · {msg.symbol}</span>}
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}

export default function SmartTrade() {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sending, setSending] = useState(false);
  const bottom = useRef();

  const loadSessions = async () => {
    try { const { data } = await api.get('/smart-trade/sessions'); setSessions(data); } catch {}
  };
  const loadHistory = async (sid) => {
    if (!sid) { setMsgs([]); return; }
    try { const { data } = await api.get(`/smart-trade/history?session_id=${sid}`); setMsgs(data); } catch {}
  };

  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { loadHistory(sessionId); }, [sessionId]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, sending]);

  const send = async (payload) => {
    const message = typeof payload === 'string' ? payload : text;
    if (!message.trim() || sending) return;
    setSending(true);
    const optimistic = { id: 'tmp-' + Date.now(), role: 'user', content: message, created_at: new Date().toISOString() };
    setMsgs(m => [...m, optimistic]);
    setText('');
    try {
      const { data } = await api.post('/smart-trade/chat', { message, session_id: sessionId });
      if (!sessionId) setSessionId(data.session_id);
      setMsgs(m => [...m, {
        id: 'ai-' + Date.now(), role: 'assistant', content: data.reply,
        symbol: data.symbol, used_live_data: data.used_live_data,
        created_at: new Date().toISOString(),
      }]);
      loadSessions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI is temporarily unavailable');
      setMsgs(m => m.filter(x => x.id !== optimistic.id));
    } finally { setSending(false); }
  };

  const newSession = () => { setSessionId(null); setMsgs([]); };
  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    await api.delete(`/smart-trade/sessions/${sid}`);
    if (sid === sessionId) newSession();
    loadSessions();
  };

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-8rem)]" data-testid="smart-trade-page">
      {/* Sessions sidebar */}
      <aside className="glass rounded-xl p-3 flex flex-col overflow-hidden">
        <Button onClick={newSession} data-testid="new-chat-btn" className="mb-3 bg-saffron hover:bg-saffron-hover text-white">
          <Plus size={16} weight="bold" className="mr-2" /> New chat
        </Button>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 mb-2">Recent</div>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 && <div className="text-xs text-muted-foreground p-2">No conversations yet.</div>}
          {sessions.map(s => (
            <div key={s.session_id}
              onClick={() => setSessionId(s.session_id)}
              className={`group p-2.5 rounded-md cursor-pointer flex items-start gap-2 ${sessionId === s.session_id ? 'bg-saffron/10 border border-saffron/30' : 'hover:bg-white/5 border border-transparent'}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{s.first_msg}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{s.count} msgs · {new Date(s.last_at).toLocaleDateString()}</div>
              </div>
              <button onClick={(e) => deleteSession(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity">
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <section className="glass rounded-xl flex flex-col overflow-hidden">
        <div className="p-4 border-b border-white/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg gradient-orange flex items-center justify-center shadow-glow-saffron">
            <Robot size={20} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg leading-none">4xView Smart Trade</div>
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              AI market analyst · BTCUSD & XAUUSD · Live data
            </div>
          </div>
          <span className="text-[10px] px-2 py-1 rounded bg-saffron/10 text-saffron uppercase tracking-widest">Claude Sonnet 4.5</span>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {msgs.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-2xl gradient-orange flex items-center justify-center shadow-glow-saffron mb-4">
                <Robot size={30} weight="fill" className="text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold">Welcome to Smart Trade</h2>
              <p className="text-muted-foreground text-sm mt-2 max-w-md">
                Ask for a trade idea on <b className="text-saffron">BTCUSD</b> or <b className="text-saffron">XAUUSD</b>. I only respond when you ask — and I only give setups when there's a genuine high-probability opportunity.
              </p>
              <div className="grid sm:grid-cols-2 gap-2 mt-6 w-full max-w-lg">
                {SUGGESTIONS.map(({ icon: I, label, q }) => (
                  <button key={label} onClick={() => send(q)} data-testid={`suggest-${label.replace(/\s/g,'-')}`}
                    className="glass p-3 rounded-lg text-left hover:border-saffron/40 border border-transparent transition-colors flex items-center gap-3">
                    <I size={18} className="text-saffron shrink-0" weight="duotone" />
                    <div>
                      <div className="text-sm font-medium">{label}</div>
                      <div className="text-xs text-muted-foreground truncate">"{q}"</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map(m => <MessageBubble key={m.id} msg={m} />)}
          {sending && (
            <div className="flex justify-start">
              <div className="glass rounded-lg p-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-saffron animate-bounce" />
                <div className="w-2 h-2 rounded-full bg-saffron animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-saffron animate-bounce" style={{ animationDelay: '300ms' }} />
                <span className="text-xs text-muted-foreground ml-2">Analyzing markets…</span>
              </div>
            </div>
          )}
          <div ref={bottom} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/5 flex gap-2">
          <Input value={text} onChange={e => setText(e.target.value)} disabled={sending}
            data-testid="smart-input" placeholder='Ask for a trade idea (e.g. "BTCUSD analysis")…'
            className="bg-surface-base border-white/10 focus:border-saffron h-11" />
          <Button type="submit" disabled={sending || !text.trim()} data-testid="smart-send"
            className="bg-saffron hover:bg-saffron-hover text-white h-11 px-6">
            <PaperPlaneRight size={16} weight="fill" />
          </Button>
        </form>
        <div className="text-[10px] text-muted-foreground px-4 pb-3 text-center">
          Educational analysis only — not financial advice. Trade at your own risk.
        </div>
      </section>
    </div>
  );
}
